/**
 * Switch house rules.
 *
 * Switch is a folk game: almost every table plays a different set, and several
 * of the popular rules compete for the same rank. So each rank gets one mode
 * rather than a pile of booleans — that way "Jacks reverse" and "Jacks change
 * suit" cannot both be switched on, because they are the same setting.
 */
export const MIN_PLAYERS = 2;
export const MAX_PLAYERS = 8;
const BASE = {
    startingHandSize: 7,
    twos: 'pickupTwo',
    sevens: 'none',
    eights: 'skip',
    queens: 'none',
    jacks: 'reverse',
    aces: 'changeSuit',
    penaltiesStack: true,
    suitChangerIsWild: true,
    draw: 'onePass',
    bluffing: false,
    playMultipleSameRank: true,
    cannotFinishOnPowerCard: false,
    turnTimeLimitSeconds: 60,
};
export const RULE_PRESETS = [
    {
        id: 'house',
        name: 'House Rules',
        description: '7 skips, 8 gives you another go, Jacks change suit, the Ace of Hearts picks up 5 unless the Ace of Diamonds blocks it, and you may bluff.',
        rules: {
            ...BASE,
            twos: 'pickupTwo',
            sevens: 'skip',
            eights: 'playAgain',
            queens: 'none',
            jacks: 'changeSuit',
            aces: 'heartsPickupFive',
            penaltiesStack: true,
            bluffing: true,
        },
    },
    {
        id: 'classic',
        name: 'Classic Switch',
        description: '2s pick up 2, 8s skip, Aces change suit, Jacks reverse the direction of play.',
        rules: { ...BASE },
    },
    {
        id: 'blackJacks',
        name: 'Black Jacks',
        description: 'The pick-up-heavy set: black Jacks sting for 5, a red Jack cancels, Aces change suit.',
        rules: { ...BASE, jacks: 'pickupFive', aces: 'changeSuit', queens: 'skip' },
    },
    {
        id: 'crazyEights',
        name: 'Crazy Eights',
        description: 'The stripped-back ancestor: 8s are wild and change the suit, nothing else has an effect.',
        rules: {
            ...BASE,
            twos: 'none',
            sevens: 'none',
            eights: 'none',
            queens: 'none',
            jacks: 'none',
            aces: 'none',
            playMultipleSameRank: false,
        },
    },
];
/** The set a fresh lobby starts with. */
export const DEFAULT_RULES = RULE_PRESETS[0].rules;
const OPTIONS = {
    twos: ['pickupTwo', 'none'],
    sevens: ['skip', 'mirror', 'none'],
    eights: ['skip', 'playAgain', 'none'],
    queens: ['skip', 'none'],
    draw: ['onePass', 'onePlay', 'untilPlayable'],
    jacks: ['reverse', 'pickupFive', 'changeSuit', 'none'],
    aces: ['changeSuit', 'heartsPickupFive', 'none'],
};
const BOOLEAN_KEYS = [
    'penaltiesStack',
    'suitChangerIsWild',
    'bluffing',
    'playMultipleSameRank',
    'cannotFinishOnPowerCard',
];
function clampInt(value, min, max, fallback) {
    const n = typeof value === 'number' ? Math.round(value) : Number.NaN;
    if (!Number.isFinite(n))
        return fallback;
    return Math.min(max, Math.max(min, n));
}
/**
 * Coerces untrusted input from a lobby host into a valid ruleset. Anything
 * unrecognised falls back to the default rather than throwing, so a stale
 * client can never wedge a lobby.
 */
export function sanitiseRules(input) {
    const raw = (input ?? {});
    const rules = { ...DEFAULT_RULES };
    for (const key of BOOLEAN_KEYS) {
        if (typeof raw[key] === 'boolean')
            rules[key] = raw[key];
    }
    const writable = rules;
    for (const [key, allowed] of Object.entries(OPTIONS)) {
        const value = raw[key];
        if (typeof value === 'string' && allowed.includes(value)) {
            // Each key's allowed list is exactly its own union, checked just above.
            writable[key] = value;
        }
    }
    rules.startingHandSize = clampInt(raw.startingHandSize, 3, 13, DEFAULT_RULES.startingHandSize);
    rules.turnTimeLimitSeconds = clampInt(raw.turnTimeLimitSeconds, 0, 300, DEFAULT_RULES.turnTimeLimitSeconds);
    if (rules.turnTimeLimitSeconds > 0 && rules.turnTimeLimitSeconds < 10) {
        rules.turnTimeLimitSeconds = 10;
    }
    return rules;
}
/** How many 52-card decks a table of this size needs to avoid running dry. */
export function decksForPlayers(playerCount, startingHandSize) {
    const needed = playerCount * startingHandSize + 20;
    return needed > 52 ? 2 : 1;
}
//# sourceMappingURL=rules.js.map